import React, { useState, useEffect } from "react"
import { connectRange } from "react-instantsearch-dom"
import { Slider, Box } from "@mui/material"

import { makeStyles } from "makeStyles"

import FilterWrapper from "./FilterWrapper"

import useDebounce from "@secureo/common/hooks/useDebounce"
import useToggle from "@secureo/common/hooks/useToggle"

const useStyles = makeStyles()({
	pricePadding: {
		border: "1px solid grey",
		borderRadius: "20px",
		padding: "5px 10px 5px 10px",
		fontSize: 14,
	},
})

// TODO: Use TextFields?
const Inputs = ({ min, max }: InputsProps) => {
	const { classes } = useStyles()
	return (
		<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
			<div className={classes.pricePadding}>{min}</div>
			<div>bis</div>
			<div className={classes.pricePadding}>{max}</div>
		</div>
	)
}

interface InputsProps {
	min: string
	max: string
}

const RangeFilter = ({
	title,
	omitWrapper,
	currentRefinement,
	min,
	max,
	refine,
	formatter = (value: number) => String(value),
	step = 1,
	hint,
	onChildItems,
}: Props) => {
	const [isInitialized, toggleIsInitialized] = useToggle(false)
	const [inputValueMinMax, setInputValueMinMax] = useState({
		min: currentRefinement.min,
		max: currentRefinement.max,
	})
	const debouncedMinMax = useDebounce(inputValueMinMax, 700)

	const isAvailable = min !== undefined && max !== undefined
	const hideFilter = !isAvailable || (!isNaN(min) === !isNaN(max) && min === max)

	const actualMin = min > inputValueMinMax.min ? min : inputValueMinMax.min
	const actualMax = max < inputValueMinMax.max ? max : inputValueMinMax.max

	useEffect(() => {
		if (isAvailable) {
			if (!isInitialized) toggleIsInitialized()
			else {
				try {
					refine(debouncedMinMax)
				} catch (err) {
					console.error(err)
				}
			}
		}
		// eslint-disable-next-line
	}, [debouncedMinMax, isAvailable])

	// Used when filters have been reset
	useEffect(() => {
		if (actualMin !== currentRefinement.min || actualMax !== currentRefinement.max) {
			setInputValueMinMax({ min: currentRefinement.min, max: currentRefinement.max })
		}

		// eslint-disable-next-line
	}, [currentRefinement.min, currentRefinement.max])

	useEffect(() => {
		if (onChildItems) {
			if (min && max && min !== max) onChildItems(1)
			else onChildItems(0)
		}

		// eslint-disable-next-line
	}, [min, max])

	const onChange = (_e: any, [min, max]: number[]) => {
		setInputValueMinMax({ min, max })
	}

	if (hideFilter) return null

	return (
		<FilterWrapper title={title} omitWrapper={omitWrapper} hint={hint}>
			<Box style={{ minWidth: 200 }}>
				<Inputs min={formatter(actualMin)} max={formatter(actualMax)} />
				<Box px={1}>
					<Slider
						min={min}
						max={max}
						step={step}
						valueLabelDisplay="off"
						valueLabelFormat={formatter}
						getAriaValueText={formatter}
						value={[actualMin, actualMax]}
						onChange={onChange}
					/>
				</Box>
			</Box>
		</FilterWrapper>
	)
}

interface Props {
	title: string
	omitWrapper: boolean
	currentRefinement: {
		min: number
		max: number
	}
	min: number
	max: number
	refine: (values: { min: number; max: number }) => void
	formatter?: (value: number) => string
	step?: number
	hint?: string
	onChildItems?: (numberOfItems: number) => void
}

export default connectRange(RangeFilter)
